import { ReactNode, useEffect, useMemo, useState } from "react";
import { Alert, Button, FormGroup, Input } from "reactstrap";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useAtomValue } from "jotai";

import {
  BrieGPTConfig,
  CalendarConfig,
  QrCodeConfig,
  ThemeSelector,
  ViewTypesWithModule,
  ViewTypesWithModuleSection,
  defaultCalendarConfig,
} from "./AddViewDrawer/AddViewDrawer";
import { GetDefaultLayout } from "../Drawers/Records/ModuleLayoutSelector";
import { isGlobalCloudAtom, tenantIDAtom } from "../../atoms/GlobalAtoms";
import CalendarConfigComponent from "./PageViewTable/CalendarConfig";
import BrieGPTViewConfig from "./PageViewBrieGPT/BrieGPTViewConfig";
import useUpdateView from "../../api/api-hooks/views/useUpdateView";
import { ReusableViewIDs, View } from "../../types/PageInterfaces";
import { callFunctionIfExists } from "../../utils/GeneralUtils";
import RequiredTitle from "../../elements/RequiredTitle";
import useView from "../../api/api-hooks/views/useView";
import AppSpinner from "../../elements/AppSpinner";
import AppCopy from "../AppComponents/AppCopy";
import useModule from "../../hooks/useModule";
import AppDrawer from "../Drawers/AppDrawer";

interface Props {
  pageID: string;
  viewID?: string;
  onClose: () => void;
  parentViewID?: string;
  isReusedView?: boolean;
}

const emptyObjetc = {} as ReusableViewIDs;

export default function ViewSettingsDrawer({
  pageID,
  viewID,
  onClose,
  parentViewID,
  isReusedView = false,
}: Props) {
  const [search] = useSearchParams();
  const { view, viewLoading } = useView({
    pageID,
    viewID,
  });
  const isGlobalCloud = useAtomValue(isGlobalCloudAtom);
  const { updateView, viewUpdating } = useUpdateView(pageID);
  const { module } = useModule(view?.ModuleAPIName);
  const client = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [query, setQuery] = useState<any>(null);
  const [layout, setLayout] = useState<any>(null);
  const [theme, setTheme] = useState("");
  const [insights, setInsights] = useState(false);
  const [calendarConfig, setCalendarConfig] = useState<CalendarConfig>({
    Fields: [],
    StartField: "",
    EndField: "",
  });
  const [qrCodeConfig, setQrCodeConfig] = useState<QrCodeConfig | null>(null);
  const [brieGPTConfig, setBrieGPTConfig] = useState<BrieGPTConfig>({
    ShowAll: false,
  });
  const [reuseViewID, setReuseViewID] = useState("");
  // To configure existing Views/SubViews to reuse other Views
  const [reusableViewIDs, setReusableViewIDs] =
    useState<ReusableViewIDs>(emptyObjetc);

  const disabled = useMemo(() => {
    const isReusableView = search.toString().includes("ReuseView");

    return isReusedView || isReusableView;
  }, [view, pageID, parentViewID, search]);

  useEffect(() => {
    setName(view?.Name || "View Name");
    setDescription(view?.Description || "");
    setQuery(
      view?.ViewFilters ? structuredClone(view?.ViewFilters) : { Filters: [] }
    );
    setTheme(view?.Theme || "");
    setInsights(view?.Insights ?? false);
    setCalendarConfig(view?.CalendarConfig || defaultCalendarConfig());
    setQrCodeConfig(view?.QrCodeConfig || null);
    setReuseViewID(view?.ReuseViewID || "");
    setReusableViewIDs(view?.ReusableViewIDs || emptyObjetc);
    setBrieGPTConfig({
      ShowAll: view?.ShowAll || false,
    });
  }, [
    view?.Name,
    view?.ViewFilters,
    view?.Theme,
    view?.Insights,
    view?.QrCodeConfig,
    view?.ReuseViewID,
    view?.ReusableViewIDs,
    view?.ShowAll,
  ]);

  useEffect(() => {
    if (!module) return;

    setLayout(GetDefaultLayout(module, view?.DefaultRecordLayout));
  }, [view?.DefaultRecordLayout, module]);

  let DrawerBody: ReactNode = null;

  if (viewLoading || !view) {
    DrawerBody = (
      <div className="h-100 w-100 d-flex justify-content-center align-items-center">
        <AppSpinner size="xl" />
      </div>
    );
  } else {
    DrawerBody = (
      <>
        {disabled && (
          <Alert color="info" className="py-3">
            This {parentViewID ? "SubView" : "View"} was inherited from another
            View in this Global App.
            <br />
            It's settings can only be modified from the original View.
          </Alert>
        )}
        <FormGroup>
          <RequiredTitle title="Name" size="md" />
          <Input
            disabled={disabled}
            placeholder="Name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            bsSize="sm"
            className="mb-4"
          />
          {isGlobalCloud && !!view?.GlobalAppID && (
            <>
              <h4>API Name</h4>
              <Input
                disabled={true}
                placeholder="Name"
                type="text"
                value={view.APIName}
                bsSize="sm"
                className="mb-4"
              />
            </>
          )}
          {isGlobalCloud && !!view?.GlobalAppID && (
            <>
              <h4>View ID</h4>
              <Input
                disabled={true}
                placeholder="View ID"
                type="text"
                value={view.id}
                bsSize="sm"
                className="mb-4"
              />
            </>
          )}
          <h4>Description</h4>
          <Input
            disabled={disabled}
            type="textarea"
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            bsSize="sm"
            rows={3}
          />
        </FormGroup>
        {ViewTypesWithModule.includes(view.Type) &&
          view?.ModuleAPIName &&
          module && (
            <ViewTypesWithModuleSection
              query={query}
              setQuery={setQuery}
              moduleAPIName={view?.ModuleAPIName}
              module={module}
              layout={layout}
              setLayout={setLayout}
              insights={insights}
              setInsights={setInsights}
              qrCodeConfig={qrCodeConfig}
              setQrCodeConfig={setQrCodeConfig}
              showQrConfig={true}
              globalAppID={view?.GlobalAppID}
              viewID={view?.id}
              reusableViews={reusableViewIDs}
              setReusableViews={setReusableViewIDs}
              disabled={disabled}
            />
          )}
        {(view.Type === "Dashboard" || insights === true) && (
          <ThemeSelector theme={theme} setTheme={setTheme} />
        )}
        {view.Type === "Calendar" && (
          <>
            <hr />
            <CalendarConfigComponent
              moduleAPIName={view?.ModuleAPIName}
              calendarConfig={calendarConfig}
              setCalendarConfig={setCalendarConfig}
            />
          </>
        )}
        {view.Type === "BrieGPT" && (
          <BrieGPTViewConfig
            brieGPTConfig={brieGPTConfig}
            setBrieGPTConfig={setBrieGPTConfig}
          />
        )}
        <h4>Other Options</h4>
        <div className="tw:flex tw:items-center tw:gap-2">
          <div className="tw:font-medium tw:text-sm">Copy View JSON: </div>
          <AppCopy text={JSON.stringify(view, null, 2)} />
        </div>
      </>
    );
  }

  return (
    <AppDrawer
      open={!!viewID}
      onClose={onClose}
      headerTitle={viewLoading ? "Loading View Settings..." : "View Settings"}
      headerOptions={
        <Button
          color="primary"
          size="sm"
          style={{ width: 90 }}
          onClick={handleUpdate}
          disabled={
            viewUpdating ||
            (!!qrCodeConfig && !qrCodeConfig.SearchField) ||
            disabled
          }
        >
          {viewUpdating ? <AppSpinner color="white" /> : "Save"}
        </Button>
      }
      bodyCSS="pt-2 pb-8"
      body={DrawerBody}
    />
  );

  function handleUpdate() {
    if (!view) return;

    let extraOnSuccess: (() => any) | undefined;
    let payload: Partial<Record<keyof View, any>> = {};
    const hasInsights = view?.hasOwnProperty("Insights") || insights === true;

    if (view?.Name !== name) {
      payload.Name = name;
    }

    if (view?.Description !== description) {
      payload.Description = description;
    }

    if (JSON.stringify(view?.ViewFilters) !== JSON.stringify(query)) {
      payload.ViewFilters = query;
    }

    if (view?.DefaultRecordLayout !== layout?.id) {
      payload.DefaultRecordLayout = layout?.id;
    }
    if (view?.Type === "Dashboard" || hasInsights) {
      payload.Theme = theme;
    }
    if (view?.Type === "BrieGPT" && !!view?.ShowAll !== brieGPTConfig.ShowAll) {
      payload.ShowAll = brieGPTConfig.ShowAll;
      extraOnSuccess = () =>
        client.invalidateQueries({
          queryKey: [tenantID, viewID, "BrieGPTChats"],
        });
    }
    if (hasInsights) {
      payload.Insights = insights;
    }

    if (view?.Type === "Calendar") {
      payload.CalendarConfig = calendarConfig;
    }

    if (
      JSON.stringify(view?.QrCodeConfig || "1") !==
      JSON.stringify(qrCodeConfig || "2")
    ) {
      payload.QrCodeConfig = qrCodeConfig;
    }

    if (view?.ReuseViewID !== reuseViewID) {
      payload.ReuseViewID = reuseViewID;
    }

    if (
      reusableViewIDs &&
      Object.keys(reusableViewIDs).length &&
      JSON.stringify(reusableViewIDs) !==
        JSON.stringify(view?.ReusableViewIDs ?? {})
    ) {
      payload.ReusableViewIDs = reusableViewIDs;
    }

    if (!Object.keys(payload).length) return;

    updateView({
      viewID: view._id ?? "",
      payload: payload,
      onSuccess: () => {
        handleOnClose();
        callFunctionIfExists(extraOnSuccess);
      },
      parentViewID,
    });
  }

  function handleOnClose() {
    onClose();
  }
}
